// Standardized error handling utilities
// Provides consistent error handling patterns across the application

import { logger } from './logger';

// Standard error codes for the application
export enum ErrorCode {
  // Authentication & Authorization
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  
  // API Errors
  API_ERROR = 'API_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  
  // Validation Errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  REQUIRED_FIELD_MISSING = 'REQUIRED_FIELD_MISSING',
  
  // Data Errors
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  CONFLICT = 'CONFLICT',
  
  // System Errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// Application-specific error class
export class AppError extends Error {
  public readonly code: ErrorCode;
  public readonly statusCode: number;
  public readonly context?: string;
  public readonly details?: any;
  public readonly timestamp: Date;

  constructor(
    message: string,
    code: ErrorCode,
    statusCode: number = 500,
    context?: string,
    details?: any
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.statusCode = statusCode;
    this.context = context || '';
    this.details = details;
    this.timestamp = new Date();

    // Maintain proper stack trace for V8 engines (Node.js, Chrome)
    if ('captureStackTrace' in Error && typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, AppError);
    }
  }

  // Convert to plain object for serialization
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      context: this.context,
      details: this.details,
      timestamp: this.timestamp.toISOString(),
      stack: this.stack,
    };
  }
}

// Error handler utility functions
export class ErrorHandler {
  // Handle API errors consistently
  static handleApiError(error: any, context: string): AppError {
    logger.error(`API Error in ${context}`, error);

    if (error instanceof AppError) {
      return error;
    }

    // Handle fetch errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return new AppError(
        'Network connection failed. Please check your internet connection.',
        ErrorCode.NETWORK_ERROR,
        0,
        context,
        error.message
      );
    }

    // Handle HTTP errors
    if (error.status) {
      const statusCode = error.status;
      let errorCode: ErrorCode;
      let message: string;

      switch (statusCode) {
        case 401:
          errorCode = ErrorCode.UNAUTHORIZED;
          message = 'Authentication required. Please log in again.';
          break;
        case 403:
          errorCode = ErrorCode.FORBIDDEN;
          message = 'You do not have permission to perform this action.';
          break;
        case 404:
          errorCode = ErrorCode.NOT_FOUND;
          message = 'The requested resource was not found.';
          break;
        case 409:
          errorCode = ErrorCode.CONFLICT;
          message = 'A conflict occurred. The resource may have been modified by another user.';
          break;
        case 422:
          errorCode = ErrorCode.VALIDATION_ERROR;
          message = 'Invalid data provided. Please check your input and try again.';
          break;
        case 500:
        default:
          errorCode = ErrorCode.INTERNAL_ERROR;
          message = 'An internal server error occurred. Please try again later.';
          break;
      }

      return new AppError(message, errorCode, statusCode, context, error);
    }

    // Handle unknown errors
    return new AppError(
      error.message || 'An unexpected error occurred',
      ErrorCode.UNKNOWN_ERROR,
      500,
      context,
      error
    );
  }

  // Handle authentication errors
  static handleAuthError(error: any, context: string): AppError {
    logger.authError(context, error);

    if (error.message?.includes('consent')) {
      return new AppError(
        'Administrator consent is required. Please contact your IT administrator.',
        ErrorCode.FORBIDDEN,
        403,
        context,
        error
      );
    }

    if (error.message?.includes('token')) {
      return new AppError(
        'Authentication token has expired. Please log in again.',
        ErrorCode.TOKEN_EXPIRED,
        401,
        context,
        error
      );
    }

    return new AppError(
      'Authentication failed. Please try logging in again.',
      ErrorCode.UNAUTHORIZED,
      401,
      context,
      error
    );
  }

  // Handle validation errors
  static handleValidationError(field: string, value: any, rule: string): AppError {
    const message = `Validation failed for field "${field}": ${rule}`;
    logger.error(message, { field, value, rule }, 'VALIDATION');

    return new AppError(
      message,
      ErrorCode.VALIDATION_ERROR,
      400,
      'VALIDATION',
      { field, value, rule }
    );
  }

  // Handle configuration errors
  static handleConfigError(setting: string, expected?: string): AppError {
    const message = expected
      ? `Configuration error: ${setting} should be ${expected}`
      : `Configuration error: ${setting} is not properly configured`;

    logger.error(message, { setting, expected }, 'CONFIG');

    return new AppError(
      message,
      ErrorCode.CONFIGURATION_ERROR,
      500,
      'CONFIG',
      { setting, expected }
    );
  }
}

// React Error Boundary compatible error logging
export function logErrorBoundary(error: Error, errorInfo: any, context: string = 'ErrorBoundary'): void {
  logger.error('React Error Boundary caught an error', {
    error: {
      name: error.name,
      message: error.message,
      stack: error.stack,
    },
    errorInfo,
  }, context);

  // In production, you might want to send this to an error reporting service
  if (!logger.getLogLevel() || logger.getLogLevel() === 'error') {
    // Send to error reporting service (e.g., Sentry, Bugsnag, etc.)
    // reportErrorToService(error, errorInfo, context);
  }
}

// Async operation wrapper with error handling
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  context: string,
  fallback?: T
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    const appError = ErrorHandler.handleApiError(error, context);
    
    // If fallback is provided, log the error but return fallback
    if (fallback !== undefined) {
      logger.warn(`Operation failed but using fallback in ${context}`, appError);
      return fallback;
    }
    
    // Otherwise, re-throw the standardized error
    throw appError;
  }
}

// Retry logic with exponential backoff
export async function withRetry<T>(
  operation: () => Promise<T>,
  maxAttempts: number = 3,
  baseDelay: number = 1000,
  context: string = 'RetryOperation'
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await operation();
      
      if (attempt > 1) {
        logger.info(`Operation succeeded on attempt ${attempt}`, undefined, context);
      }
      
      return result;
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxAttempts) {
        logger.error(`Operation failed after ${maxAttempts} attempts`, lastError, context);
        throw ErrorHandler.handleApiError(lastError, context);
      }

      const delay = baseDelay * Math.pow(2, attempt - 1);
      logger.warn(`Attempt ${attempt} failed, retrying in ${delay}ms`, error, context);
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but TypeScript needs it
  throw ErrorHandler.handleApiError(lastError!, context);
}

// Validation helpers
export const validators = {
  required: (value: any, fieldName: string): void => {
    if (value === null || value === undefined || value === '') {
      throw ErrorHandler.handleValidationError(fieldName, value, 'is required');
    }
  },

  email: (email: string, fieldName: string = 'email'): void => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw ErrorHandler.handleValidationError(fieldName, email, 'must be a valid email address');
    }
  },

  minLength: (value: string, minLength: number, fieldName: string): void => {
    if (value.length < minLength) {
      throw ErrorHandler.handleValidationError(
        fieldName, 
        value, 
        `must be at least ${minLength} characters long`
      );
    }
  },

  maxLength: (value: string, maxLength: number, fieldName: string): void => {
    if (value.length > maxLength) {
      throw ErrorHandler.handleValidationError(
        fieldName, 
        value, 
        `must not exceed ${maxLength} characters`
      );
    }
  },

  url: (url: string, fieldName: string = 'url'): void => {
    try {
      new URL(url);
    } catch {
      throw ErrorHandler.handleValidationError(fieldName, url, 'must be a valid URL');
    }
  },
};

// Export commonly used functions
export { logger };
export default ErrorHandler;
